/**
 * sessionStorage mirror of the in-flight export jobs.
 *
 * There is no list endpoint by agreement with the backend, so this is the only
 * way polling survives a remount. Every access is guarded: storage throws in
 * private browsing, when the quota is exceeded, and when it is disabled
 * outright.
 *
 * Only the fields the poller needs are stored, and a record that does not match
 * the expected shape is dropped rather than repaired — a malformed entry would
 * otherwise poll an endpoint forever with a key nothing can settle.
 */
import { EXPORT_JOBS_STORAGE_KEY } from '../config/exportConstants';
import type { ExportJobRecord, ReportKind, ExportScope } from '../types';

const REPORT_KINDS: ReadonlySet<string> = new Set<ReportKind>([
    'calendar',
    'plan_metric',
    'promo_metric',
    'plan_comparison',
]);

const SCOPES: ReadonlySet<string> = new Set<ExportScope>(['current_view', 'all_data']);

const isExportJobRecord = (value: unknown): value is ExportJobRecord => {
    if (typeof value !== 'object' || value === null) return false;
    const record = value as Partial<ExportJobRecord>;
    return (
        typeof record.exportId === 'string' &&
        record.exportId.length > 0 &&
        typeof record.planId === 'string' &&
        typeof record.createdAt === 'number' &&
        Number.isFinite(record.createdAt) &&
        typeof record.reportKind === 'string' &&
        REPORT_KINDS.has(record.reportKind) &&
        typeof record.scope === 'string' &&
        SCOPES.has(record.scope)
    );
};

export const readExportJobs = (): ExportJobRecord[] => {
    try {
        const raw = sessionStorage.getItem(EXPORT_JOBS_STORAGE_KEY);
        if (!raw) return [];
        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(isExportJobRecord);
    } catch {
        return [];
    }
};

export const writeExportJobs = (jobs: ExportJobRecord[]): void => {
    try {
        if (jobs.length === 0) {
            sessionStorage.removeItem(EXPORT_JOBS_STORAGE_KEY);
            return;
        }
        sessionStorage.setItem(EXPORT_JOBS_STORAGE_KEY, JSON.stringify(jobs));
    } catch {
        // Persistence is best-effort: the in-memory list still drives polling
        // for as long as the provider stays mounted.
    }
};
